import styles from './Ticket.module.scss';

import qs from 'query-string';
import { useRef } from 'react';
import classNames from 'classnames';
import { RxDragHandleDots2 } from 'react-icons/rx';

import { EventTierCreation } from 'legacy/events/types';

import { EventPricingTier } from 'legacy/common/types/models';
import Toggle from 'legacy/common/components/form/Toggle';
import { currencyFormat } from 'legacy/common/utils/currency';
import { MdContentCopy, MdLockOpen } from 'react-icons/md';

import { copyToClipboard } from 'legacy/common/utils/clipboard';
import * as toast from 'legacy/common/utils/toast';

import config from 'config';

type TicketProps = {
  ticket: EventPricingTier | EventTierCreation;
  testId?: string;
  dataTestId?: string;
  eventId?: string;
  eventShareToken?: string;
  onClick: () => void;
  toggleTicketTier: (isDisabled: boolean) => void;
  isSortable?: boolean;
  toggleHideTier: () => void;
  lastCreatedTicketId?: string;
};

const Ticket: React.FC<TicketProps> = ({
  eventId,
  eventShareToken,
  ticket,
  testId,
  onClick,
  toggleTicketTier,
  isSortable,
  toggleHideTier,
  lastCreatedTicketId
}) => {
  const ticketRef = useRef<HTMLDivElement>();

  const params = qs.stringify({
    tierShareToken: ticket.shareToken,
    invite: eventShareToken
  });
  const shareUrl = `${config.SITE_URL}events/${eventId}?${params}`;

  const ticketId = (ticket as EventTierCreation).id;

  return (
    <div onClick={onClick} data-testid={testId} ref={ticketRef}>
      <div
        className={classNames('relative w-full', {
          'opacity-60': ticket.isDisabled
        })}
      >
        <div
          className={classNames(
            'w-full flex justify-between relative cursor-pointer border-1 rounded-md p-4 border-gray-200 hover:border-gray-500',
            {
              [styles.tierAnimation]: lastCreatedTicketId === ticketId
            }
          )}
        >
          {isSortable && (
            <div className="absolute top-4 right-4">
              <RxDragHandleDots2
                size={20}
                className="cursor-grab text-gray-400"
              />
            </div>
          )}

          <div className="w-full">
            <div className="flex items-center">
              <div>
                <div className="flex items-start font-normal text-base">
                  {ticket.isPrivate && (
                    <span data-testid={`${testId}-lock-icon`}>
                      <MdLockOpen size={20} className="mr-1" />
                    </span>
                  )}

                  <span data-testid={`${testId}-name`}>{ticket.name}</span>
                </div>

                <div className="text-base font-semibold">
                  {currencyFormat(ticket.price)}
                </div>
              </div>
            </div>

            {ticket.isPrivate && (
              <div className="flex mt-4 font-semibold text-gray-500">
                <div
                  className="mr-2 flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(ticket.shareToken);

                    toast.showToast({
                      type: 'success',
                      message: 'Private code copied to clipboard'
                    });
                  }}
                >
                  <span>Private code: {ticket.shareToken}</span>
                  <div className="px-2">
                    <MdContentCopy size={16} className="text-primary-600" />
                  </div>
                </div>

                <div
                  className="flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();

                    copyToClipboard(shareUrl);
                    toast.showToast({
                      type: 'success',
                      message: 'Unlock URL copied to clipboard'
                    });
                  }}
                >
                  Unlock URL
                  <div className="px-2">
                    <MdContentCopy size={16} className="text-primary-600" />
                  </div>
                </div>
              </div>
            )}

            {ticket.description && (
              <div
                data-testid={`${testId}-description`}
                className="h-6 text-gray-400 text-base font-normal mt-3 truncate"
              >
                {ticket.description}
              </div>
            )}

            <div className="mt-4 flex items-center">
              <div className="flex items-center text-gray-500 mr-4 text-sm">
                <span className="mr-1">🎟</span>
                <span>{(ticket as EventPricingTier)?.totalGuests || 0}</span>
                {typeof ticket.maxAttendance === 'number' &&
                  ticket.maxAttendance > 0 && (
                    <span data-testid={`${testId}-maxAttendance`}>
                      /{ticket.maxAttendance}
                    </span>
                  )}
                <span className="ml-1">sold</span>
              </div>

              {ticket.requiresApproval && (
                <div className="text-sm text-gray-500">
                  👍 Requires approval{' '}
                  {ticket.questions?.length > 0 &&
                    `(${ticket.questions?.length} q's)`}
                </div>
              )}
            </div>

            <div className="mt-4 w-full border border-gray-50" />

            <div className="flex flex-row items-center justify-between mt-4">
              <span className="text-sm text-gray-500">Pause tier sales</span>

              <div className="ml-2">
                <Toggle
                  isActive={ticket?.isDisabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (ticket?.isDisabled) {
                      toggleTicketTier(false);
                      return;
                    }

                    toggleTicketTier(true);
                  }}
                />
              </div>
            </div>
            <div className="flex flex-row items-center justify-between mt-4">
              <span className="text-sm text-gray-500">Hide pricing tier</span>

              <div className="ml-2">
                <Toggle
                  isActive={!ticket?.isVisible}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleHideTier();
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ticket;
